import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Megaphone, CalendarDays, FileText, Image as ImageIcon, Music, Flower2, Video, Share2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { POST_TYPES, bnNum } from "@/constants/site";
import { useLang } from "@/lib/lang";

const ICONS = {
  image: ImageIcon,
  video: Video,
  megaphone: Megaphone,
  calendar: CalendarDays,
  file: FileText,
  music: Music,
  flower: Flower2,
};

export default function FeedSection() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { lang, t } = useLang();

  useEffect(() => {
    API.get("/posts", { params: { limit: 6 } })
      .then(({ data }) => setPosts(data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const dateFmt = (iso) => {
    const d = new Date(iso);
    if (lang === "en") return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    return `${bnNum(d.getDate())}·${bnNum(d.getMonth() + 1)}·${bnNum(d.getFullYear())}`;
  };

  const share = (p) => {
    const url = `${window.location.origin}/#updates`;
    const text = encodeURIComponent(`${p.title}\n${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <section id="updates" className="py-20 sm:py-28 bg-[#1E0A0E]/40 border-y border-[#F2B134]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>{t("feed_eyebrow")}</Eyebrow>
            <h2 className="font-bengali font-extrabold text-3xl sm:text-5xl mt-4">
              {t("feed_title_a")} <span className="gold-text">{t("feed_title_b")}</span>
            </h2>
          </div>
          <p className="font-bengali text-sm text-[#D1BBA7]/80 max-w-sm">{t("feed_sub")}</p>
        </Reveal>

        {loaded && posts.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-dashed border-[#F2B134]/30 p-10 text-center" data-testid="feed-empty-state">
            <Megaphone className="mx-auto text-[#F2B134]/60" size={28} />
            <p className="font-bengali text-[#D1BBA7] mt-4">{t("feed_empty")}</p>
          </div>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
            {posts.map((p, i) => {
              const Icon = ICONS[POST_TYPES[p.type]?.icon] || Megaphone;
              const isImg = p.media?.length > 0 && p.media[0].kind === "image";
              const typeLabel = lang === "en" ? POST_TYPES[p.type]?.labelEN : POST_TYPES[p.type]?.label;
              return (
                <Reveal key={p.id} delay={i * 0.05} className="h-full">
                  <article className="h-full flex flex-col rounded-2xl border border-[#F2B134]/18 bg-[#120507] overflow-hidden hover:border-[#F2B134]/45 transition-colors group" data-testid={`feed-card-${p.id}`}>
                    {isImg && (
                      <div className="h-40 overflow-hidden">
                        <img src={p.media[0].url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                    )}
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2">
                        <span className="h-7 w-7 rounded-lg bg-[#2B0F15] border border-[#F2B134]/25 grid place-items-center text-[#F2B134]">
                          <Icon size={13} />
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.18em] text-[#F2B134]/80">{typeLabel || p.type}</span>
                        {p.pinned && <span className="ml-auto text-[10px] font-bold text-[#D7261E] border border-[#D7261E]/50 rounded-full px-2 py-0.5">{t("feed_pinned")}</span>}
                      </div>
                      <h3 className="font-bengali font-bold text-lg mt-3 leading-snug">{p.title}</h3>
                      {p.body && <p className="font-bengali text-sm text-[#D1BBA7] mt-2 line-clamp-3 leading-relaxed">{p.body}</p>}
                      <div className="mt-auto pt-4 flex items-center justify-between">
                        <span className="text-xs text-[#D1BBA7]/60 tabular-nums">
                          {p.event_date ? (lang === "bn" ? bnNum(p.event_date) : p.event_date) : dateFmt(p.created_at)}
                        </span>
                        <button
                          onClick={() => share(p)}
                          data-testid={`feed-share-${p.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D1BBA7] hover:text-[#F2B134] transition-colors"
                        >
                          <Share2 size={13} /> {t("feed_share")}
                        </button>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
